import { formatDurationFromSeconds } from "../../work-object-field/model/duration-field";
import type { WorkObjectFieldDefinition } from "../../work-object-field/model/use-work-object-fields";

export const emptyFieldValueLabel = "—";

// Values that reference something else are stored as ids. The board payload
// ships a label for every referenced id, so this resolves them; anything left
// unresolved falls back to a placeholder rather than exposing a raw id.
export const unresolvedReferenceLabel = "Unknown";

export type FieldValueSource = {
  fieldValues: Array<{ fieldDefinitionId: string; value: unknown }>;
  relationLabels?: Record<string, string>;
};

export function fieldValueValues(item: FieldValueSource, fieldId: string): string[] {
  const value = item.fieldValues.find((fieldValue) => fieldValue.fieldDefinitionId === fieldId)?.value;
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  return typeof value === "string" && value ? [value] : [];
}

// One place decides how a field value reads. Every surface — table cell, card,
// group header, timeline bar, tooltip — goes through it, so a field can never
// render as an id in one view and a name in another.
export function formatFieldValueParts(
  item: FieldValueSource,
  field: WorkObjectFieldDefinition,
  values = fieldValueValues(item, field.id),
): string[] {
  if (values.length === 0) return [];

  switch (field.type) {
    case "select":
    case "multi_select":
      return values.map((value) => field.options.find((option) => option.id === value)?.value ?? value);
    case "user_relation":
    case "user_multi_relation":
    case "work_object_relation":
    case "work_object_multi_relation":
      return values.map((value) => referenceLabel(item, value));
    case "date":
    case "date_range":
      return values.map(formatFieldDate);
    case "duration":
      return values.map((value) => formatDurationFromSeconds(value)).filter(Boolean);
    case "percent":
      return values.map((value) => `${value}%`);
    case "currency":
      return values.map((value) => `$${value}`);
    case "rating": {
      const scale = readRatingScale(field);
      return values.map((value) => `${clampRating(Number(value), scale)}/${scale}`);
    }
    case "checkbox":
      return values.map((value) => (value === "true" ? "Yes" : "No"));
    default:
      return values;
  }
}

export function formatFieldValue(item: FieldValueSource, field: WorkObjectFieldDefinition): string {
  const parts = formatFieldValueParts(item, field);
  if (parts.length === 0) return emptyFieldValueLabel;
  return field.type === "date_range" ? parts.join(" – ") : parts.join(", ");
}

export function referenceLabel(item: FieldValueSource, id: string): string {
  return item.relationLabels?.[id] ?? unresolvedReferenceLabel;
}

// Group headers key on a raw value: option ids resolve to their option, and
// anything referencing a person or an item resolves through the same map.
export function formatGroupValue(
  field: WorkObjectFieldDefinition | undefined,
  value: string,
  labels: Record<string, string> = {},
): string {
  if (!field || value === "") return "No value";
  if (field.type === "select" || field.type === "multi_select") {
    return field.options.find((option) => option.id === value)?.value ?? value;
  }
  if (isReferenceFieldType(field.type)) {
    return labels[value] ?? unresolvedReferenceLabel;
  }
  return `${field.name}: ${value}`;
}

export function isReferenceFieldType(type: WorkObjectFieldDefinition["type"]) {
  return (
    type === "user_relation" ||
    type === "user_multi_relation" ||
    type === "work_object_relation" ||
    type === "work_object_multi_relation"
  );
}

export function formatFieldDate(value: string) {
  const date = new Date(value.includes("T") ? value : `${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
}

function readRatingScale(field: WorkObjectFieldDefinition): 5 | 10 | 100 {
  const scale = (field.config as { ratingScale?: 5 | 10 | 100 } | null)?.ratingScale;
  return scale === 10 || scale === 100 ? scale : 5;
}

function clampRating(value: number, scale: number) {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(Math.round(value), scale);
}
